import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { foundSelector } from 'store/reducers/nlp/nlp-reducer';
import {
  filtersSelector,
  pushResources,
  setResources,
  setDefaultResources,
  setShouldFetch,
  setShouldGetMoreResources,
  setUuids,
  shouldFetchSelector,
  shouldGetMoreResourcesSelector,
  currentPageSelector,
  setCurrentPage,
  IResourcesFilters,
  setIsResourcesLoading,
  setIsMoreResourcesLoading,
  allCurrentFiltersSelector,
  isResourcesLoadingSelector,
  setResourcesError,
  emptyAllFilters,
  setOriginalResources,
  originalResourcesSelector,
  selectionFiltersSelector,
  setCurrentStep,
  currentStepSelector,
  groupedFiltersSelector,
  filteredUuidsSelector,
  exclusiveFiltersSelector,
  setDefaultUuids,
} from 'store/reducers/resources/resources-reducer';

import useResources from './useResources';
import useResourcesSamples from './useResourcesSamples';

export const SAMPLES_PER_PAGE = 17;

export default function useResourcesContent() {
  const dispatch = useDispatch();
  const filters = useSelector(filtersSelector);
  const exclusiveFilters = useSelector(exclusiveFiltersSelector);
  const shouldFetch = useSelector(shouldFetchSelector);
  const shouldGetMoreResources = useSelector(shouldGetMoreResourcesSelector);
  const currentPage = useSelector(currentPageSelector);
  const allCurrentFilters = useSelector(allCurrentFiltersSelector);
  const isResourcesLoading = useSelector(isResourcesLoadingSelector);
  const selectedCriteria = useSelector(selectionFiltersSelector);
  const originalResources = useSelector(originalResourcesSelector);
  const found = useSelector(foundSelector);
  const currentStep = useSelector(currentStepSelector);
  const groupedFilters = useSelector<any>(groupedFiltersSelector);
  const filteredUuids = useSelector(filteredUuidsSelector);
  const [uuidsToFetch, setUuidsToFetch] = useState<string[]>([]);
  const [currentFilters, setCurrentFilters] = useState<
    IResourcesFilters & { groupings?: { uuid: string; type: string }[] }
  >(
    Object.keys(allCurrentFilters).reduce((acc, key) => {
      return (acc[key] = allCurrentFilters[key]);
    }, {})
  );
  const getGroupings = useCallback(() => {
    const groupings = Object.values(groupedFilters).map((group, index) => {
      const groupName = Object.keys(groupedFilters)[index];

      return group.map((item) => ({
        uuid: item.uuid,
        type: item.datatype,
        group_name: groupName,
      }));
    });

    return groupings.reduce((acc, group) => acc.concat(group), []);
  }, [groupedFilters]);

  const resourcesQuery = useResources(currentFilters, {
    enabled: Object.keys(currentFilters).length > 0 || getGroupings().length > 0,
    onSettled: (data, error) => {
      if (data && !isLoading && !isError) {
        dispatch(setUuids(data.collection));
        dispatch(setDefaultUuids(data.collection));
      }
    },
  });

  const { data, isLoading, isRefetching, isError, isRefetchError } = resourcesQuery;
  const {
    data: samples,
    isLoading: isMoreResourcesLoading,
    refetch: refetchSamples,
    isRefetching: isSamplesRefetching,
  } = useResourcesSamples(uuidsToFetch, { enabled: uuidsToFetch.length > 0 });

  useEffect(() => {
    dispatch(setResourcesError(isError || isRefetchError));
  }, [isError, isRefetchError]);

  useEffect(() => {
    if (shouldFetch) {
      const groupings = getGroupings();
      setCurrentFilters({ ...allCurrentFilters, groupings });
      dispatch(setShouldFetch(false));
    }
  }, [filters, shouldFetch, allCurrentFilters]);

  useEffect(() => {
    dispatch(setCurrentPage(1));
    setUuidsToFetch([]);
  }, [currentFilters]);

  /**
   * Reset list when all filters are cleared
   */
  useEffect(() => {
    if (Object.values(filters).every((f) => f.length === 0) && !shouldFetch) {
      if (!found) {
        dispatch(emptyAllFilters({}));
        dispatch(setShouldFetch(true));
      }
    }
  }, [filters, found]);

  useEffect(() => {
    if (exclusiveFilters) {
      const groupings = getGroupings();
      setCurrentFilters({ ...allCurrentFilters, groupings });
    }
  }, [exclusiveFilters]);

  useEffect(() => {
    dispatch(setIsResourcesLoading(isLoading || isRefetching));
  }, [isLoading, data, isRefetching]);

  useEffect(() => {
    dispatch(setIsMoreResourcesLoading(isMoreResourcesLoading || isSamplesRefetching));
    if (isResourcesLoading && !isMoreResourcesLoading && !isSamplesRefetching && data) {
      dispatch(setIsResourcesLoading(false));
    }
  }, [isMoreResourcesLoading, samples, isSamplesRefetching, data]);

  useEffect(() => {
    if (data && !isRefetching) {
      dispatch(setUuids(data.collection));
      dispatch(setResources(data.samples));
      dispatch(setOriginalResources(data.samples));
      dispatch(setDefaultResources(data.samples));
      if (
        Object.keys(allCurrentFilters).some((key) => allCurrentFilters[key].length > 0) &&
        currentStep === 1
      ) {
        dispatch(setCurrentStep(currentStep + 1));
      }
    }
  }, [data, isRefetching]);

  useEffect(() => {
    if (selectedCriteria === null) {
      dispatch(setResources(originalResources));
    }
  }, [selectedCriteria]);

  useEffect(() => {
    if (samples && samples?.resources.length > 0 && !isSamplesRefetching) {
      dispatch(pushResources(samples.resources));
    }
  }, [samples, isSamplesRefetching]);

  useEffect(() => {
    if (shouldGetMoreResources && currentStep === 4) {
      setUuidsToFetch(
        filteredUuids
          .map((u) => u.uuid)
          .slice(currentPage * SAMPLES_PER_PAGE, (currentPage + 1) * SAMPLES_PER_PAGE)
      );
      dispatch(setCurrentPage(currentPage + 1));
      dispatch(setShouldGetMoreResources(false));
    }
  }, [shouldGetMoreResources, currentStep]);

  useEffect(() => {
    if (found && Object.keys(found).length === 1 && found.people && found.people.length > 0) {
      dispatch(setResources([]));
    }
  }, [found]);
}
